import React from 'react';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  adrenalGauge,
  adrenalPatterns,
  AdrenalPattern,
  AdrenalMarkerArrow,
} from '../../models/StressAdrenalAnalysis';
import { getAdrenalPatternInsight } from '../../models/adrenal-pattern-insights.generated';
import { LabsmartsGuage } from '../charts/LabsmartsGauge';
import { MARKER_ROW_STYLE } from './pattern-box-styles';

// Up/down arrows for the marker row — same assets the Glucose Insights panel uses.
const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

const arrowStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px',
};

type Props = { bloodworkAnalysis: BloodworkAnalysis };

// Gauge-label prefix per direction. The likelihood word + percent are appended
// by the gauge, so this reads e.g. "Adrenal Underactivity is Likely (80%)".
const CONDITION_LABEL: Record<string, string> = {
  underactivity: 'Adrenal Underactivity',
  overactivity: 'Adrenal Overactivity',
  reserve: 'Reduced Stress Resilience',
  // Relational by design: names the measured cortisol-to-reserve relationship
  // without claiming a cause (stress) or a mechanism (exhaustion).
  dominance: 'Cortisol Dominance',
  balanced: 'Adrenal Imbalance',
};

export class StressAdrenalRegulationInsights extends React.Component<Props, {}> {
  /*
   * Explains the gauge — mirrors the Thyroid Insights header info-circle Popover
   * (same icon, placement, and styling).
   *
   * THIS COPY DESCRIBES adrenalGauge() IN StressAdrenalAnalysis.ts. Read the
   * function before editing it, and edit it whenever the function changes: this
   * popover taught the pre-2026-08-23 rule ("Na/K is primary and sets the
   * direction") for months after cortisol was made to lead, and a changelog
   * entry was written off the stale version.
   *
   * The three claims below are each a line of that function:
   *   • cortisol leads     — `cortisolDir !== null ? cortisolDir : nakDir`
   *   • Na/K alone is weak — NAK_ONLY_MAX caps a lone Na/K at 68 (Possible)
   *   • reserve reads last — DHEA-S / pregnenolone steer only when functionDir
   *                          is null, i.e. cortisol AND Na/K are both silent
   *   • dominance          — `cortisolDir === 'high' && reserveLow`, the one
   *                          condition whose tally counts opposing markers
   *
   * NOTHING RENDERS BETWEEN THE GAUGE AND THE PATTERN BOXES. `gauge.markers`
   * exists but is never drawn: it feeds the AI export and the ADRENAL_GAUGE_IDS
   * signature only. Two sentences here used to say the driving markers "appear
   * beneath the gauge", which described a row that has never been on screen.
   * Markers belong to the boxes, by decision (mary, 2026-09-02) — do not add a
   * second row under the needle repeating them.
   */
  gaugeInfoPopover = () => (
    <Popover id="stress-adrenal-gauge-info" style={{ maxWidth: '340px' }}>
      <Popover.Title as="div" style={{ fontSize: '13px', fontWeight: 700, backgroundColor: '#fff', borderBottom: 'none' }}>
        About this gauge
      </Popover.Title>
      <Popover.Content>
        <div style={{ textAlign: 'left', fontSize: '12px', color: '#555', lineHeight: 1.45 }}>
          <div style={{ marginBottom: '8px' }}>
            This gauge reads the adrenal axis on the same 0–100% scale as the other panels.{' '}
            <strong>Cortisol</strong> and <strong>aldosterone</strong> (read via the{' '}
            <strong>Na/K ratio</strong>) are the hormones the adrenals defend first, so they set whether
            the glands are keeping up. <strong>Cortisol is primary and sets the direction:</strong>
          </div>
          <ul style={{ paddingLeft: '16px', marginBottom: '8px' }}>
            <li><strong>Below optimal</strong> — points toward adrenal underactivity.</li>
            <li><strong>Above optimal</strong> — points toward adrenal overactivity.</li>
            <li>
              <strong>Within optimal, or not measured</strong> — the <strong>Na/K ratio</strong> sets the
              direction instead.
            </li>
          </ul>
          <div style={{ marginBottom: '8px' }}>
            Serum Na/K also moves for reasons that are not adrenal, so when it is the only marker off it
            reads as supporting evidence and the needle stays below <strong>likely</strong>.
          </div>
          <div style={{ marginBottom: '8px' }}>
            <strong>DHEA-S</strong> and <strong>Pregnenolone</strong> are the adrenals' backup and raw material; the
            body draws on them before essential function slips. When cortisol and Na/K read optimal, low levels show
            as <strong>reduced stress resilience</strong> (an earlier signal) and high levels as overactivity. A
            deviation in the same direction pushes the needle higher (possible &rarr; likely &rarr; highly
            likely); the markers behind each finding are named in the boxes below.
          </div>
          <div style={{ marginBottom: 0 }}>
            <strong>Cortisol Dominance</strong> reads a high cortisol alongside a low{' '}
            <strong>DHEA-S</strong> or <strong>Pregnenolone</strong>. It names the measured relationship
            between the two and claims nothing about a cause.
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );

  static gaugeInput(ba: BloodworkAnalysis) {
    return {
      cortisolAM: ba.cortisolAM,
      cortisolPM: ba.cortisolPM,
      dheaS: ba.dheaS,
      pregnenolone: ba.pregnenolone,
    };
  }

  /**
   * Whether this panel renders anything. Mirrors render()'s own guard: the gauge
   * needs at least one usable adrenal marker (Na/K, cortisol, DHEA-S or
   * pregnenolone). Callers use it to drop the evaluation column entirely rather
   * than leave an empty gap.
   */
  static shouldRender(ba: BloodworkAnalysis): boolean {
    if (!ba?.bloodwork) return false;
    return !!adrenalGauge(
      ba.sodiumPotassiumRatio,
      StressAdrenalRegulationInsights.gaugeInput(ba),
    );
  }

  /**
   * Serializable view of this panel for one test — the gauge and its pattern
   * boxes — mirroring render() exactly: the same gauge call, the same patterns,
   * and boxes without an authored insight dropped the way renderPatternBoxes
   * drops them. Used by the raw-data JSON export (AIExportBuilder), which is
   * why it returns plain data rather than nodes. Null when the panel would not
   * render at all, so the export omits the key instead of writing an empty one.
   */
  static exportInsights(ba: BloodworkAnalysis): {
    gauge: {
      label: string;
      condition: string;
      category: string;
      probability: number;
      markers: AdrenalMarkerArrow[];
    };
    patterns: Array<{
      categoryId: number;
      title: string;
      bullets: string[];
      markers: AdrenalMarkerArrow[];
    }>;
  } | null {
    if (!ba?.bloodwork) return null;
    const input = StressAdrenalRegulationInsights.gaugeInput(ba);
    const gauge = adrenalGauge(ba.sodiumPotassiumRatio, input);
    if (!gauge) return null;
    const patterns = adrenalPatterns(ba.sodiumPotassiumRatio, input)
      .map(pattern => ({ pattern, insight: getAdrenalPatternInsight(pattern.categoryId) }))
      .filter(x => !!x.insight)
      .map(({ pattern, insight }) => ({
        categoryId: pattern.categoryId,
        title: StressAdrenalRegulationInsights.patternTitle(insight!),
        bullets: insight!.bullets,
        markers: pattern.markers,
      }));
    return {
      gauge: {
        label: `${CONDITION_LABEL[gauge.condition]} is`,
        condition: gauge.condition,
        category: gauge.category,
        probability: gauge.probability,
        markers: gauge.markers,
      },
      patterns,
    };
  }

  /*
   * ── PDF pagination ─────────────────────────────────────────────────────────
   *
   * The PDF packer needs this panel's height BEFORE React renders it, the same
   * way Glucose and Nutrients self-estimate. A fixed number would mis-paginate,
   * because the panel grows with every pattern box that fires.
   *
   * Every constant below was MEASURED off the rendered panel at its real 300px
   * width (headless Chrome over the compiled custom.scss). The set it replaces
   * was short by 7-19%, worst on the case that matters most: a mixed-direction
   * panel matches no combination row, so it falls back to ONE BOX PER MARKER,
   * and four boxes render 831px against an estimated 676. Under-counting is the
   * direction that breaks the document, because the packer then prints the next
   * section into a page this one has already filled.
   *
   * This panel only began printing to the PDF when pdf-section.tsx stopped
   * gating the Insights panel on an RBC/WBC/GLUCOSE allowlist, so the old
   * numbers had never actually paginated anything.
   */
  // Card header + .card-body padding + the 5px panel border, top and bottom.
  static readonly PDF_CARD_CHROME = 93;
  // .anemiaStatusContainer's min-height. The gauge alone already clears it, so
  // it only binds if the gauge is ever made smaller.
  static readonly PDF_MIN_BODY_HEIGHT = 150;
  // The Highcharts container, which takes the height its own options declare
  // (chart.height 200 in LabsmartsGuage).
  static readonly PDF_GAUGE_HEIGHT = 200;
  // Bootstrap's body line-height (1.5) at the panel's 12px font.
  static readonly PDF_LINE_HEIGHT = 18;
  // A pattern box minus its text: border, padding, and the ul's own 1rem
  // bottom margin, which Bootstrap's reboot supplies and nothing here resets.
  static readonly PDF_PATTERN_BOX_HEIGHT = 32;
  // .condition-section's `margin: 10px 0`. Boxes collapse against each other, so
  // it is paid once before each box and once more after the last one.
  static readonly PDF_PATTERN_BOX_MARGIN = 10;
  // Average glyph width at 12px sans-serif. The whole box is `font-weight: bold`,
  // so this is the bold figure (measured at 6.6, carried at 6.9 so the wrap
  // arithmetic rounds up).
  static readonly PDF_CHAR_WIDTH = 6.9;
  // Text width inside a pattern box, and inside a bullet (which also gives up
  // its glyph and the 3px margin after it).
  static readonly PDF_BOX_WIDTH = 254;
  static readonly PDF_BULLET_WIDTH = 246;
  // Per marker in the ▲/▼ row: the glyph with its margins, plus the ", " that
  // separates it from the one before.
  static readonly PDF_MARKER_SLOT_WIDTH = 13;
  static readonly PDF_MARKER_SEPARATOR_CHARS = 2;

  /** Rendered lines a string wraps to in `width` px at the panel's body font. */
  static pdfLines(text: string, width: number): number {
    return Math.max(
      1,
      Math.ceil(
        (text.length * StressAdrenalRegulationInsights.PDF_CHAR_WIDTH) / width,
      ),
    );
  }

  /**
   * The box's heading, which is the title alone unless there are bullets for it
   * to introduce. Kept beside renderPatternBoxes, which builds the same string.
   */
  static patternTitle(insight: { title: string; bullets: string[]; consistentWith: boolean }): string {
    return insight.bullets.length > 0 && insight.consistentWith
      ? `${insight.title} Consistent with:`
      : insight.title;
  }

  /** The ▲/▼ row under a box, which wraps between whole markers. */
  static markerRowHeight(markers: AdrenalMarkerArrow[]): number {
    if (!markers.length) return 0;
    const chars =
      markers.reduce((n, m) => n + m.label.length, 0) +
      (markers.length - 1) * StressAdrenalRegulationInsights.PDF_MARKER_SEPARATOR_CHARS;
    const width =
      markers.length * StressAdrenalRegulationInsights.PDF_MARKER_SLOT_WIDTH +
      chars * StressAdrenalRegulationInsights.PDF_CHAR_WIDTH;
    const lines = Math.max(
      1,
      Math.ceil(width / StressAdrenalRegulationInsights.PDF_BOX_WIDTH),
    );
    return lines * StressAdrenalRegulationInsights.PDF_LINE_HEIGHT;
  }

  /** One pattern box, including the margin above it. */
  static patternBoxHeight(pattern: AdrenalPattern): number {
    const insight = getAdrenalPatternInsight(pattern.categoryId);
    if (!insight) return 0;
    const S = StressAdrenalRegulationInsights;
    const bullets = insight.bullets.reduce(
      (h, bullet) => h + S.pdfLines(bullet, S.PDF_BULLET_WIDTH) * S.PDF_LINE_HEIGHT,
      0,
    );
    return (
      S.PDF_PATTERN_BOX_MARGIN +
      S.PDF_PATTERN_BOX_HEIGHT +
      S.pdfLines(S.patternTitle(insight), S.PDF_BOX_WIDTH) * S.PDF_LINE_HEIGHT +
      bullets +
      S.markerRowHeight(pattern.markers)
    );
  }

  /**
   * This panel's rendered height, in the capture pixels every other number
   * in the PDF's page arithmetic uses (see pdfPanelMetrics).
   */
  static estimatedPdfHeight(ba: BloodworkAnalysis): number {
    const S = StressAdrenalRegulationInsights;
    if (!S.shouldRender(ba)) return 0;
    const patterns = adrenalPatterns(ba.sodiumPotassiumRatio, S.gaugeInput(ba));
    const boxes = patterns.reduce(
      (sum, pattern) => sum + S.patternBoxHeight(pattern),
      0,
    );
    return (
      S.PDF_CARD_CHROME +
      Math.max(S.PDF_GAUGE_HEIGHT + boxes, S.PDF_MIN_BODY_HEIGHT) +
      // The last box's bottom margin collapses out of the panel body but is
      // stopped by the card's own padding, so it still costs the page.
      (boxes ? S.PDF_PATTERN_BOX_MARGIN : 0)
    );
  }


  renderArrow = (direction: 'up' | 'down', label: string) => {
    const src = direction === 'up' ? arrow_up : arrow_down;
    const alt = direction === 'up' ? '↑' : '↓';
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        <img src={src} alt={alt} style={arrowStyle} />
        {label}
      </span>
    );
  };

  // Client-facing pattern boxes under the gauge — mirrors the Glucose Insights
  // panel: "{title} Consistent with:" + bullet findings + the driving markers
  // (triangle left of each name). One box per firing pattern; nothing renders in
  // the balanced state (no pattern fires).
  renderPatternBoxes = (patterns: AdrenalPattern[]) => {
    return patterns.map((pattern) => {
      const insight = getAdrenalPatternInsight(pattern.categoryId);
      if (!insight) return null;
      return (
        <div className="condition-section" key={pattern.categoryId}>
          <div style={{ color: '#000', textAlign: 'left' }}>
            {StressAdrenalRegulationInsights.patternTitle(insight)}
          </div>
          {insight.bullets.length > 0 && (
            <ul style={{ paddingLeft: 0, marginLeft: 0, listStyle: 'none', textAlign: 'left' }}>
              {insight.bullets.map((bullet) => (
                // Hanging indent, matching the Glucose and Nutrient Status
                // boxes: a wrapped line stays clear of the glyph instead of
                // running back under it. The longest bullet shipping today
                // ("Higher Sodium Relative to Potassium") measures ~241px in a
                // 246px box, so this is one character away from mattering.
                <li key={bullet} style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ display: 'inline-block', marginRight: '3px' }}>•</span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          )}
          <div style={MARKER_ROW_STYLE}>
            {pattern.markers.map((m, index) => (
              <React.Fragment key={`${m.label}-${index}`}>
                {index > 0 && <>,{' '}</>}
                {this.renderArrow(m.direction, m.label)}
              </React.Fragment>
            ))}
          </div>
        </div>
      );
    });
  };

  render() {
    const { bloodworkAnalysis: ba } = this.props;
    if (!ba.bloodwork) return null;

    // Null only when NO adrenal marker is usable: Na/K, cortisol AM/PM, DHEA-S
    // and pregnenolone are all unmeasured or range-less. Na/K on its own is not
    // required (adrenalGauge reads whichever of the five it is given).
    //
    // gaugeInput() rather than a fourth inline copy of the same object:
    // shouldRender, exportInsights and estimatedPdfHeight all build their input
    // through it, and render drifting from those three is what let the screen
    // and the PDF disagree about this panel in the first place.
    const input = StressAdrenalRegulationInsights.gaugeInput(ba);
    const gauge = adrenalGauge(ba.sodiumPotassiumRatio, input);
    if (!gauge) return null;

    const patterns = adrenalPatterns(ba.sodiumPotassiumRatio, input);

    return (
      // Standard 300px eval panel — same size as the Glucose / Immune / RBC panels.
      <Card className="evaluationPanel" id="stress-adrenal-regulation-insights">
        <Card.Header className="evaluationPanelHeading">
          Stress &amp; Adrenal Insights
          <OverlayTrigger placement="bottom" overlay={this.gaugeInfoPopover()}>
            <FontAwesomeIcon
              icon="info-circle"
              style={{ cursor: 'pointer', color: '#fff', fontSize: 14, marginLeft: 8, verticalAlign: 'middle' }}
            />
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            <LabsmartsGuage
              text={`${CONDITION_LABEL[gauge.condition]} is`}
              chartContainerId="stressAdrenalRegulationGuage"
              bloodwork={ba.bloodwork}
              probability={gauge.probability}
              category={gauge.category}
              ticks={[0, 50, 70, 90]}
            />
            {this.renderPatternBoxes(patterns)}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
